import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, fmtTime } from "@/lib/api";
import { SectionTitle, Badge } from "@/components/Bits";

// Classify a log row into one of the buckets the UI renders distinctly.
// decision values we know about:
//   OPEN           — entry placed
//   BLOCKED        — entry rejected pre-flight
//   SKIPPED        — conditions didn't fire (or idle)
//   EXIT           — position closed (see event_log.log_position_exit)
//   RECOVERED      — engine recovered from an error (SYSTEM)
//   orphan_reconciled                       — local OPEN reconciled vs exchange (SYSTEM)
//   recovered_position_without_entry_log    — exchange position w/ no audit row
//   AUDIT_WRITE_FAILED                      — entry_audits write failed (CRITICAL)
function classify(row) {
  const d = (row?.decision || "").toString();
  if (d === "EXIT") return "EXIT";
  if (d === "OPEN" || d === "RESIZED_OPEN") return "ENTRY";
  // Audit-recovery rows describe ENTRIES that lack an audit trail — surface
  // them in the ENTRY tab so operators don't miss them while filtering.
  if (d === "recovered_position_without_entry_log" || d === "AUDIT_WRITE_FAILED") return "ENTRY";
  if (d === "BLOCKED") return "BLOCKED";
  if (d === "SKIPPED") return "SKIPPED";
  // Anything else (RECOVERED, orphan_reconciled, …) is treated as SYSTEM.
  return "SYSTEM";
}

// Tailwind classes for each bucket — dark theme friendly.
const BUCKET_CLASS = {
  ENTRY: { dot: "bg-[#00E676]", text: "text-[#00E676]", ring: "border-[#00E676]/30" },
  BLOCKED: { dot: "bg-[#F59E0B]", text: "text-[#F59E0B]", ring: "border-[#F59E0B]/30" },
  SKIPPED: { dot: "bg-[#71717A]", text: "text-[#A0A0AB]", ring: "border-border" },
  EXIT: { dot: "bg-[#9CA3AF]", text: "text-[#E4E4E7]", ring: "border-border" }, // colored by PnL sign below
  SYSTEM: { dot: "bg-[#F59E0B]", text: "text-[#F59E0B]", ring: "border-[#F59E0B]/30" },
};

function exitTone(row) {
  const pnl = Number(row?.pnl_usdt);
  if (Number.isFinite(pnl)) {
    if (pnl > 0) return { text: "text-[#00E676]", dot: "bg-[#00E676]", ring: "border-[#00E676]/30" };
    if (pnl < 0) return { text: "text-[#FF3B30]", dot: "bg-[#FF3B30]", ring: "border-[#FF3B30]/30" };
  }
  return BUCKET_CLASS.EXIT;
}

export default function SignalLogs() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("ALL");

  const load = async () => {
    try { setRows(await api.signalLogs(200) || []); } catch {}
  };
  useEffect(() => { load(); const id = setInterval(load, 4000); return () => clearInterval(id); }, []);

  const clear = async () => {
    try { await api.clearSignalLogs(); toast.success("Cleared"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const filtered = useMemo(() => {
    if (filter === "ALL") return rows;
    return rows.filter((r) => classify(r) === filter);
  }, [rows, filter]);

  const FILTER_TABS = ["ALL", "ENTRY", "EXIT", "BLOCKED", "SKIPPED", "SYSTEM"];

  return (
    <div className="p-6 space-y-6">
      <SectionTitle
        sub="Full event timeline: signal evaluated → entry blocked or executed → position opened → exit triggered → position closed → PnL recorded."
        right={<Button data-testid="clear-signals-btn" variant="outline" onClick={clear} className="rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633]">Clear logs</Button>}
      >
        Signal Logs
      </SectionTitle>

      <div className="flex items-center gap-2 flex-wrap" data-testid="signal-logs-filters">
        {FILTER_TABS.map((f) => (
          <button
            key={f}
            data-testid={`signal-logs-filter-${f.toLowerCase()}`}
            onClick={() => setFilter(f)}
            className={`text-xs font-mono px-3 py-1.5 rounded-sm border transition-colors ${
              filter === f
                ? "border-[#00E676]/50 bg-[#00E676]/10 text-[#00E676]"
                : "border-border bg-[#0F0F14] text-[#A0A0AB] hover:text-white"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {filtered.length === 0 ? (
          <div className="bg-[#0F0F14] border border-border rounded-sm p-6 text-sm text-[#71717A]">
            No signal logs match the current filter.
          </div>
        ) : filtered.map((s) => {
          const bucket = classify(s);
          const tone = bucket === "EXIT" ? exitTone(s) : BUCKET_CLASS[bucket];
          const side = s.side_evaluated || s.side;
          return (
            <div
              key={s.id}
              data-testid={`signal-log-${s.id}`}
              data-bucket={bucket}
              className={`bg-[#0F0F14] border rounded-sm p-4 ${tone.ring}`}
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`inline-flex h-2 w-2 rounded-full ${tone.dot}`} />
                  <span className={`text-[11px] font-mono font-semibold tracking-wider uppercase ${tone.text}`}>
                    {bucket}
                  </span>
                  {side ? (
                    <Badge kind={side === "LONG" ? "long" : side === "SHORT" ? "short" : "off"}>
                      {side}
                    </Badge>
                  ) : null}
                  {s.strategy_name && s.strategy_name !== "—" ? (
                    <span className="text-xs font-mono text-[#A0A0AB]" data-testid="signal-log-strategy">
                      {s.strategy_name}
                    </span>
                  ) : null}
                  {bucket !== "EXIT" ? (
                    <Badge kind={s.decision === "OPEN" ? "on" : s.decision === "BLOCKED" ? "warn" : "off"}>
                      {s.decision}
                    </Badge>
                  ) : null}
                  <span className="text-sm text-[#A0A0AB] truncate" title={s.reason}>{s.reason}</span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {bucket === "EXIT" && Number.isFinite(Number(s.pnl_usdt)) ? (
                    <span
                      data-testid="signal-log-pnl"
                      className={`text-xs font-mono font-semibold ${tone.text}`}
                    >
                      {Number(s.pnl_usdt) >= 0 ? "+" : ""}
                      {Number(s.pnl_usdt).toFixed(2)} USDT
                      {Number.isFinite(Number(s.pnl_percent))
                        ? ` (${Number(s.pnl_percent) >= 0 ? "+" : ""}${Number(s.pnl_percent).toFixed(2)}%)`
                        : ""}
                    </span>
                  ) : (s.reference_price ?? s.price ?? s.btc_price) !== undefined && (s.reference_price ?? s.price ?? s.btc_price) !== null ? (
                    (() => {
                      // 2026-02 P1 — multi-asset symbol identity. NEVER
                      // hardcode "BTC". Derive label from base_asset
                      // (backend canonical) or symbol stripped of
                      // USDT suffix. Render "UNKNOWN_SYMBOL" with a
                      // warning class when the row has no symbol at
                      // all — better than a silent BTC fallback.
                      const base = s.base_asset
                        || (s.symbol ? String(s.symbol).replace(/(USDT|USDC|USD|BUSD)$/, "") : null)
                        || "UNKNOWN_SYMBOL";
                      const price = s.reference_price ?? s.price ?? s.btc_price;
                      const cls = base === "UNKNOWN_SYMBOL"
                        ? "text-xs font-mono text-orange-400"
                        : "text-xs font-mono text-[#A0A0AB]";
                      const title = base === "UNKNOWN_SYMBOL"
                        ? "Symbol missing on this legacy signal row — UI cannot infer asset safely"
                        : `Reference price for ${s.symbol || base}`;
                      return (
                        <span className={cls} title={title} data-testid={`signal-log-symbol-price-${s.id}`}>
                          {base} ${Number(price).toLocaleString("en-US", { maximumFractionDigits: 2 })}
                        </span>
                      );
                    })()
                  ) : null}
                  <span className="font-mono text-xs text-[#71717A]">{fmtTime(s.timestamp)}</span>
                </div>
              </div>

              {/* EXIT details row — price / size / order id */}
              {bucket === "EXIT" ? (
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] font-mono text-[#A0A0AB]">
                  {s.entry_price ? <span>entry <span className="text-white">{Number(s.entry_price).toFixed(2)}</span></span> : null}
                  {s.exit_price ? <span>exit <span className="text-white">{Number(s.exit_price).toFixed(2)}</span></span> : null}
                  {s.size ? <span>size <span className="text-white" data-testid={`signal-log-size-${s.id}`}>{Number(s.size).toFixed(6)} {s.base_asset || (s.symbol ? String(s.symbol).replace(/(USDT|USDC|USD|BUSD)$/, "") : "?")}</span></span> : null}
                  {s.notional_usdt ? <span>notional <span className="text-white">{Number(s.notional_usdt).toFixed(2)} USDT</span></span> : null}
                  {s.exchange_order_id ? <span>order <span className="text-white">{s.exchange_order_id}</span></span> : null}
                </div>
              ) : null}

              {/* Condition breakdown (entry cycles only) */}
              {bucket !== "EXIT" && bucket !== "SYSTEM" && (s.conditions || []).length > 0 ? (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
                  {(s.conditions || []).map((c, i) => (
                    <div key={i} className="text-xs flex items-start gap-2 border border-border/60 rounded-sm p-2">
                      <Badge kind={!c.enabled ? "off" : c.passed ? "on" : "warn"}>
                        {!c.enabled ? "OFF" : c.passed ? "PASS" : "FAIL"}
                      </Badge>
                      <div className="min-w-0">
                        <div className="font-medium">{c.name}</div>
                        <div className="text-[#A0A0AB] break-words" title={c.detail}>{c.detail || "—"}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
